import t from 'react-translate';
import { useCallback, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'react-bootstrap';
import { AngularServicesContext } from 'react-app';
import { FormProvider, useForm } from 'react-hook-form';
import { css } from '@emotion/core';

// redux
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';
import { copyLesson, moveLesson } from 'redux/actions/collections';
import { RootState } from 'redux/schemas';

import { LessonTransferModalActionType } from 'content-library/components/lecture-page/lesson-action-dropdown';
import NvModal, { ModalType } from 'shared/components/nv-modal';
import CollectionListModalBody,
{
  CollectionListModalType,
  ModalActionType,
} from 'courses/components/course-lesson-to-collection/collection-list-modal-body';

type LessonTransferModalProps = {
  show: boolean,
  setShow: Function,
  lessonTransferModalType: LessonTransferModalActionType,
  lessonId: number,
  folderId?: number,
  setLectureData: Function,
  currentCatalogId: string,
  hasExternalSubmissionLinks?: boolean,
};

const modalStyles = css`
  .bs4-modal-body {
    overflow-y: clip;
  }
`;

const LessonTransferModal = (props: LessonTransferModalProps) => {
  const {
    show,
    setShow,
    lessonTransferModalType,
    lessonId,
    folderId,
    setLectureData,
    hasExternalSubmissionLinks,
  } = props;
  const { $state } = useContext(AngularServicesContext);
  const dispatch = useDispatch();

  const methods = useForm({
    mode: 'all',
    defaultValues: {
      destinationCollectionName: null,
      destinationCollectionId: null,
      destinationCatalogId: null,
      destinationFolderId: null,
    },
  });

  const { reset, getValues } = methods;

  const lecturePage = useSelector((state: RootState) => state.models.lecturePages[lessonId]);

  const getHeader = () => {
    switch (lessonTransferModalType) {
      case LessonTransferModalActionType.COPY_LESSON:
        return t.INSTITUTIONS.CONTENT_LIBRARY.COLLECTIONS_HOME.COPY_LESSON_HEADER();
      case LessonTransferModalActionType.MOVE_LESSON:
        return t.INSTITUTIONS.CONTENT_LIBRARY.COLLECTIONS_HOME.MOVE_LESSON_HEADER();
      default:
        return null;
    }
  };

  const getDescription = () => {
    switch (lessonTransferModalType) {
      case LessonTransferModalActionType.COPY_LESSON:
        return t.INSTITUTIONS.CONTENT_LIBRARY.COLLECTIONS_HOME.COPY_LESSON_DESCRIPTION();
      case LessonTransferModalActionType.MOVE_LESSON:
        return t.INSTITUTIONS.CONTENT_LIBRARY.COLLECTIONS_HOME.MOVE_LESSON_DESCRIPTION();
      default:
        return null;
    }
  };

  const getModalActionType = () => {
    switch (lessonTransferModalType) {
      case LessonTransferModalActionType.COPY_LESSON:
        return ModalActionType.COPY;
      case LessonTransferModalActionType.MOVE_LESSON:
        return ModalActionType.MOVE;
      default:
        return null;
    }
  };

  const handleClose = useCallback(() => {
    setShow(false);
    reset();
  }, [setShow, reset]);

  const redirectToCollectionLesson = useCallback((stateParams) => {
    window.open($state?.href(
      'content-library-collection-home',
      stateParams,
    ), '_blank');
  }, [$state]);

  const moveLessonDispatchAction = useCallback(() => {
    dispatch(moveLesson({
      destinationCatalogId: getValues('destinationCatalogId'),
      destinationFolderId: getValues('destinationFolderId'),
      sourceFolderId: folderId,
      lessonId,
    })).then((res) => {
      if (!res.error) {
        const { payload } = res;
        setLectureData((prevState) => prevState.filter((eachLectureId) => eachLectureId !== lessonId));
        const {
          destinationCollectionId,
          destinationCatalogId,
        } = getValues();

        dispatch(addAlertMessage({
          type: AlertMessageType.SUCCESS,
          message: getValues('destinationFolderId')
            ? t.INSTITUTIONS.CONTENT_LIBRARY.COLLECTIONS_HOME.MOVE_LESSON_SUCCESS_MSG(lecturePage?.title)
            : t.INSTITUTIONS.CONTENT_LIBRARY.COLLECTIONS_HOME.FOLDER_CREATED_AND_MOVED_LESSON(),
          button: (
            <Button
              onClick={() => redirectToCollectionLesson(
                {
                  collectionId: destinationCollectionId,
                  catalogId: destinationCatalogId,
                  folderId: payload?.folderId, // destination folder id
                  lessonId,
                },
              )}
            >
              {t.INSTITUTIONS.CONTENT_LIBRARY.COLLECTIONS_HOME.GO_TO_COLLECTION()}
            </Button>
          ),
        }));
      }
      handleClose();
    });
  }, [
    dispatch,
    folderId,
    getValues,
    handleClose,
    lecturePage?.title,
    lessonId,
    redirectToCollectionLesson,
    setLectureData,
  ]);

  const openAssignmentWarningModal = useCallback(() => {
    dispatch(openConfirmationDialog({
      title: t.INSTITUTIONS.CONTENT_LIBRARY.COLLECTIONS_HOME.SUBMISSION_CAROUSAL_LESSON_TRANSFER_INFO_HEADER(),
      bodyText: t.INSTITUTIONS.CONTENT_LIBRARY.COLLECTIONS_HOME.SUBMISSION_CAROUSAL_LESSON_TRANSFER_INFO_DESCRIPTION(),
      confirmText: t.INSTITUTIONS.CONTENT_LIBRARY.COLLECTIONS_HOME.GOT_IT(),
      icon: 'success',
      noCancelButton: true,
      confirmButtonVariant: 'primary',
      onConfirm: () => {
        moveLessonDispatchAction();
      },
    }));
  }, [dispatch, moveLessonDispatchAction]);

  const submitAction = useCallback(() => {
    switch (lessonTransferModalType) {
      case LessonTransferModalActionType.COPY_LESSON:
        dispatch(copyLesson({
          destinationCatalogId: getValues('destinationCatalogId'),
          destinationFolderId: getValues('destinationFolderId'),
          lessonId,
        })).then((res) => {
          if (!res.error) {
            const {
              destinationCollectionName,
            } = getValues();

            dispatch(addAlertMessage({
              type: AlertMessageType.INFO,
              message: t.INSTITUTIONS.CONTENT_LIBRARY.COLLECTIONS_HOME.COPY_LESSON_STARTED(destinationCollectionName, lecturePage?.title),
            }));
          }
          handleClose();
        });

        break;
      case LessonTransferModalActionType.MOVE_LESSON:
      {
        if (hasExternalSubmissionLinks) {
          openAssignmentWarningModal();
        } else {
          moveLessonDispatchAction();
        }
        break;
      }
      default:
        break;
    }
  }, [
    lessonTransferModalType,
    dispatch,
    getValues,
    lessonId,
    lecturePage?.title,
    handleClose,
    hasExternalSubmissionLinks,
    moveLessonDispatchAction,
    openAssignmentWarningModal,
  ]);

  return (
    <div>
      <NvModal
        width={800}
        show={show}
        bodyClassName='p-0'
        type={ModalType.DYNAMIC}
        onClose={handleClose}
        header={getHeader()}
        modalStyles={modalStyles}
        fullHeight={false}
        body={(
          <FormProvider {...methods}>
            <form>
              <CollectionListModalBody
                bodyDescription={getDescription()}
                collectionListModalType={CollectionListModalType.LESSON_TRANSFER}
                modalActionType={getModalActionType()}
                submitAction={submitAction}
                onClose={handleClose}
              />
            </form>
          </FormProvider>
        )}
      />
    </div>
  );
};

export default LessonTransferModal;
